import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { format, parseISO, fromUnixTime, formatDistanceToNow } from 'date-fns';
import {CopyToClipboard} from 'react-copy-to-clipboard';

import mixpanel from 'mixpanel-browser';

// Firebase
import { db, functions } from '../../config/firebase-config';
import { collection, getDocs, getDoc, addDoc, updateDoc, doc, setDoc, deleteDoc, query, where, onSnapshot, orderBy, startAfter, limit } from 'firebase/firestore';
import { httpsCallable } from "firebase/functions";

// Redux
import { connect } from 'react-redux';

// Actions
import { getUserById } from '../../actions/authActions';
import { addLike, deletePost } from '../../actions/postActions';
import { toggleAuthModal } from '../../actions/navActions';
import { setAlert } from '../../actions/alertActions';
import { createChat, addMessage  } from '../../actions/chatActions';

// components - imported
import { Avatar, Button } from '@material-ui/core';
import ImageBlock from '../common/ImageBlock';
import DefaultAvatar from 'react-avatar';
import Verification_Badge from '../common/Verification_Badge';
import Tag from '../common/Tag';

// Icons - imported
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import RepeatIcon from '@material-ui/icons/Repeat';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import FavoriteIcon from '@material-ui/icons/Favorite';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import ShareIcon from '@material-ui/icons/Share';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import LinkIcon from '@material-ui/icons/Link';
import CheckIcon from '@material-ui/icons/Check';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';
import MailIcon from '@material-ui/icons/Mail';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';

// Modal
import Modal from '../modal/Modal';
import ModalContainer from '../modal/ModalContainer';

// DraftJS
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";

// Constant Variables
import MAIN_FEEDS from '../../utils/constant_variables/MAIN_FEEDS';

const Post = ({ 
    postDoc, 
    auth, 
    post: {
        hiddenComments
    },
    nav: {
        page,
        website,
        website_loading
    },
    getUserById, 
    parentPost,
    addLike, 
    deletePost, 
    setAlert, 
    createChat, 
    addMessage,
    toggleAuthModal,
    executeScroll,
    isPreview,
    show_pro_tools
}) => {

    // Window Width
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Tracks the like status of post
    const [liked, setLiked] = useState(false);
    // Flag for checking if like is properly updated
    const [checkLike, setCheckLike] = useState(false);

    // Flag for checking if group's name should display as author of post
    const [showGroupName, setShowGroupName] = useState(false);

    // --- Convert DraftJS text to HTML text

    // Tracks whether the text has been converted
    const [gotText, setGotText] = useState(false);
    // Stores the converted text
    const [textState, setTextState] = useState(null);
    const [rawTextState, setRawTextState] = useState(null);

    // Check if post has image - for font size reasons
    const [hasImg, setHasImg] = useState(false);
    // Flag for checking if hasImg is properly updated
    const [checkHasImg, setCheckHasImg] = useState(false);
    const [imgHeight, setImgHeight] = useState(200);

    // Toggle Dropdwon
    const [dropdown, setDropdown] = useState(false);
    const [shareDropdown, setShareDropdown] = useState(false);

    // Stores the height of a toggle menu
    const [menuHeight, setMenuHeight] = useState(null);

    // Delete post Modal toggles
    const [deleteModal, setDeleteModal] = useState(false);

    // Toggle 'start convo' modal
    const [messageModal, setMessageModal] = useState(false);

    // Tracks the status of copying
    const [copied, setCopied] = useState(false);

    // Controls the visibility of a copied modal
    const [copiedModal, setCopiedModal] = useState(false);

    // Get parent post for like page purposes and displaying the 'replying to'
    const [gotParentPost, setGotParentPost] = useState(false);

    const [replyToPost, setReplyToPost] = useState(null);

    // -- Text Truncation Functions ---

    // is text expanded?
    const [isExpanded, setIsExpanded] = useState(false);
    const CHARACTER_LIMIT = 200;
    const [shouldTruncate, setShouldTruncate] = useState(false);

    // -- END: Text Truncation Functions ---

    // -- Analytics stuff -- 
    
    // Track if Action has been tracked yet, (stores postId)
    const trackedViews = useRef(new Set());
    const trackedDms = useRef(new Set());
    const trackedCopiedLinks = useRef(new Set());
    const trackedLinkClicks = useRef(new Set());
    const trackedProfileVisits = useRef(new Set());

    // -- End: Analytics stuff --
    

    useEffect(() => {

        // Add an event listener for window resize
        window.addEventListener('resize', () => handleWindowSizeChange());

        // Clean up the event listener on component unmount
        return () => window.removeEventListener('resize', () => handleWindowSizeChange());
    }, []);

    // Clears the copied modal after a delay
    useEffect(() => {
        setTimeout(() => {
            setCopiedModal(false);
        }, 2000);
    }, [copiedModal]);

    // Resets the share dropdown and copied status after a delay
    useEffect(() => {
        setTimeout(() => {
            setShareDropdown(false);
            setCopied(false);
        }, 1000);
    }, [copied]);

    // Flag for checking if group's name should display as author of post
    useEffect(() => {

        // Don't show group's name as author if post is a paid ad
        if(!postDoc.isSponsored && !website && postDoc.group?.name) {
            for (const feed_type of MAIN_FEEDS) {
                if(page.includes(feed_type)) {
                    setShowGroupName(true);
                    break;
                }
            }
        }
    }, [page]);

    const getParentPost = async () => {
        const docRef = doc(db, 'posts', postDoc.post_commented_on);
        const postData = await getDoc(docRef);
        setReplyToPost({ ...postData.data(), _id: postData.id });
    }

    if(!gotParentPost && postDoc.post_commented_on) {
        if(!parentPost?.user) {
            getParentPost();
        } else {
            setReplyToPost(parentPost)
        }

        setGotParentPost(true);
    }

    // Update the window width state on window resize
    const handleWindowSizeChange = () => {
        setWindowWidth(window.innerWidth);
    };

    // Determine window size breakpoints
    const isMobile = windowWidth <= 769;
    const isTablet = windowWidth <= 1000;

    // ----- Post Actions Logic -------
    
    // Handle post like action
    const handleLike = (postId) => {
        if(isPreview) return;

        if(auth.user) {
            addLike({ postId, fromUserData: auth.user, postData: postDoc, website });

            // Toggles the like status
            setLiked(!liked);
        } else {

            // Retrieves user information by ID
            getUserById(postDoc.user._id);

            // Toggles the authentication modal for liking
            toggleAuthModal('like');
        }
    }

    // Closes the dropdown menu
    const toggleCopiedModal = () => {
        setDropdown(false)

        // -- Analytics stuff -- 

        // Only track analytics if post is an active Ad
        if(postDoc.isSponsored && postDoc._id) {
            console.log('TRACKING POST ANALYTICS')
            safeTrackPostAnalytics('copied_link', postDoc._id);
        }

        // -- END: Analytics stuff -- 
        
        // Sets an alert message indicating that the content has been copied to the clipboard
        // setCopiedModal(!copiedModal);
        setAlert('Copied to clipboard', `${!auth.loading && auth.isAuthenticated ? 'okay' : 'success'}`);
        
    }

    // Closes the share dropdown
    const handleShareCopy = () => {
        setShareDropdown(false);

        // -- Analytics stuff -- 

        // Only track analytics if post is an active Ad
        if(postDoc.isSponsored && postDoc._id) {
            console.log('TRACKING POST ANALYTICS')
            safeTrackPostAnalytics('copied_link', postDoc._id);
        }

        // -- END: Analytics stuff -- 
        
        // Sets an alert message indicating that the content has been copied to the clipboard
        // setCopiedModal(!copiedModal);
        setAlert('Copied to clipboard', `${!auth.loading && auth.isAuthenticated ? 'okay' : 'success'}`);
    }

    // Toggle the delete post confirmation modal
    const toggleDeleteModal = () => {
        setDropdown(false)

        // Toggles the delete modal
        setDeleteModal(!deleteModal); 
    }

    // Deletes the post
    const handleDelete = () => {
        if(postDoc) {
            let stored_id = postDoc._id

            deletePost(postDoc._id);

            // Mixpanel event tracking for deleting a post
            if (process.env.NODE_ENV === 'production') {
                mixpanel.track("Delete Post", {
                    "Post ID": `${stored_id}`
                });
            }
        }

        // Closes the delete modal
        setDeleteModal(false);
    }

    // ----- END: Post Actions Logic -------

    const handleExternalLink = (url_link) => {
        console.log('About to Redirect..');

        // -- Analytics stuff -- 

        // Only track analytics if post is an active Ad
        if(postDoc.isSponsored && postDoc._id) {
            console.log('TRACKING POST ANALYTICS')
            safeTrackPostAnalytics('external_link_click', postDoc._id);
        }

        // -- END: Analytics stuff -- 

        window.open(url_link, "_blank");
    }

    const handleProfileVisit = async () => {
        if(!isPreview) {

            console.log('About to Visit Profile..');

            // -- Analytics stuff -- 

            // Only track analytics if post is an active Ad
            if(postDoc.isSponsored && postDoc._id) {
                console.log('TRACKING POST ANALYTICS')
                await safeTrackPostAnalytics('profile_visit', postDoc._id);
            }

            // -- END: Analytics stuff -- 

            window.location.href = `/user/${postDoc.user._id}`;
        }
    }
    
    const handleGroupVisit = async () => {
        if(!isPreview) {

            console.log("About to Visit Group page.."); 

            window.location.href = `/groups/${postDoc.group?._id}?show=posts`;
        }
    }

    // Message Logic
    
    const toggleMessageModal = () => {
        setDropdown(false);

        // Get user by ID if the message modal is not currently open
        if(!messageModal) getUserById(postDoc.user._id);
        
        setMessageModal(!messageModal);
    }

    const continueMessage = () => {
        if(auth.user) {

            // If the user is authenticated, create a new chat
            createNewChat();
        } else {

            // If the user is not authenticated, close the message modal and show the authentication modal
            setMessageModal(false);
            toggleAuthModal('message');
        }
    }

    const createNewChat = async () => {
        try {
            const chatsCollectionRef = collection(db, "chats");

            // Check if chat with 2 users already exists by chat ID

            console.log('CHECK USER CHAT HERE')
            
            // Query chats where the current user is the from_user and the modal user is the to_user
            const chatsQuery = query(chatsCollectionRef, where("from_user._id", "==", auth.user._id), where("to_user._id", "==", auth.modalUser._id));

            // Query chats where the modal user is the from_user and the current user is the to_user
            const otherChatsQuery = query(chatsCollectionRef, where("to_user._id", "==", auth.user._id), where("from_user._id", "==", auth.modalUser._id));

            // Query chats where the current user is the from_user and the modal user is the to_user
            const chatsSnapshot = await getDocs(chatsQuery);

            // Map the document snapshots to an array of chat objects, including the document ID
            const chats = chatsSnapshot.docs.map((doc) => ({...doc.data(), _id: doc.id}));

            // Query chats where the modal user is the from_user and the current user is the to_user
            const otherChatsSnapshot = await getDocs(otherChatsQuery);

            // Map the document snapshots to an array of chat objects, including the document ID
            const otherChats = otherChatsSnapshot.docs.map((doc) => ({...doc.data(), _id: doc.id}));

            console.log('CHECKING CHATS DATA');
            console.log(chats);
            
            console.log('CHECKING OTHER CHATS DATA');
            console.log(otherChats);

            console.log('CHECK USER CHAT HERE PART 2')

            let chatRoomId;

            // If array length > 0 then there is already existing chat & redirect instead
            
            if(chats.length > 0) {

                // -- Share post with user ---

                // Send a message with postID attached
                console.log('Sharing post...')
                await addMessage({ formData: { postId: postDoc._id }, imgData: null, imgDimensions: null, chatId: chats[0]._id, userObj: auth.user, notify_toId: postDoc.user._id, website });

                // -- END: Share post with user ---
            } else if (otherChats.length > 0) {

                // -- Share post with user ---

                // Send a message with postID attached
                console.log('Sharing post...')
                await addMessage({ formData: { postId: postDoc._id }, imgData: null, imgDimensions: null, chatId: otherChats[0]._id, userObj: auth.user, notify_toId: postDoc.user._id, website });

                // -- END: Share post with user ---
            } else {

                // Create a new chat if no existing chat is found
                chatRoomId = await createChat(auth.modalUser, auth.user, postDoc._id);

                // -- Share post with user ---

                // Send a message with postID attached
                console.log('Sharing post...')
                console.log('ID: ', chatRoomId)
                await addMessage({ formData: { postId: postDoc._id }, imgData: null, imgDimensions: null, chatId: chatRoomId, userObj: auth.user, notify_toId: postDoc.user._id, website });

                // -- END: Share post with user ---
            }

            // -- Analytics stuff --

            // Only track analytics if post is an active Ad
            if(postDoc.isSponsored && postDoc._id) {
                console.log('TRACKING POST ANALYTICS')
                safeTrackPostAnalytics('direct_message', postDoc._id);
            }

            // -- END: Analytics stuff --

            // -- Redirect to chat page --

            if(chats.length > 0) {
                window.location.href = `/messages/${chats[0]._id}`;
            } else if (otherChats.length > 0) {
                window.location.href = `/messages/${otherChats[0]._id}`;
            } else {
                // Redirect to the chat page with the newly created chat room ID
                if(chatRoomId) {
                    window.location.href = `/messages/${chatRoomId}`;
                } else {
                    // Fallback: If something goes wrong go to messages page
                    window.location.href = `/messages`;
                }
            }

            // -- END: Redirect to chat page --

            setMessageModal(!messageModal);
            
            console.log('SHOULD BE DONE CREATING');
        } catch (error) {
            console.log('Failed to create new chat');
            // console.log(error);
            setAlert('Unable to start conversation', 'danger');
        }
    }

    // End

    // -- Text Truncation Functions ---
    
    // Helper function
    const getPlainText = (html) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    // -- END: Text Truncation Functions ---

    // -- Analytics stuff --
    
    const safeTrackPostAnalytics = async (actionType, postId) => {
        // Logic for not calling more than once per session
        
        if(actionType === 'view') {
            if (trackedViews.current.has(postId)) return;
            trackedViews.current.add(postId);
        } else if(actionType === 'direct_message') {
            if (trackedDms.current.has(postId)) return;
            trackedDms.current.add(postId);
        } else if(actionType === 'copied_link') {
            if (trackedCopiedLinks.current.has(postId)) return;
            trackedCopiedLinks.current.add(postId);
        } else if(actionType === 'external_link_click') {
            if (trackedLinkClicks.current.has(postId)) return;
            trackedLinkClicks.current.add(postId);
        } else if(actionType === 'profile_visit') {
            if (trackedProfileVisits.current.has(postId)) return;
            trackedProfileVisits.current.add(postId);
        } else {
            console.log('No valid actionType');
            return;
        }
        
        await trackPostAnalytics(actionType, postId);
    };

    const trackPostAnalytics = async (actionType, postId) => {
        const trackAnalytics = httpsCallable(functions, "trackPostAnalytics");
        try {
            const ipAddress = await getVisitorIP();
            if(ipAddress?.length > 0 && postId && actionType) {
                await trackAnalytics({ actionType: actionType, postId: postId, ipAddress: ipAddress, userId: auth?.user?._id });
            }
        } catch (err) {
            console.error("Error 473:", err);
        }
    }

    const getVisitorIP = async () => {
        try {
            const response = await fetch('https://api.ipify.org');
            const data = await response.text();

            // Store IP in State
            return data;
        } catch (error) {
            console.error('CODE 511', error)
            return null;
        }
    }

    // -- END: Analytics stuff --

    // Function to Capitalize the first letter of a string
    const capitalizeFirstLetter =(string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    const detect_Links_phoneNumbers_Addresses =(string) => {
        // Regex to match URLs
        const urlRegex = /(?<![@\w])(?:https?:\/\/|www\.)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<]*)?(?![\w@])/gi;
        
        // Regex for phone numbers (basic US-style, adjust if needed)
        // Matches formats like: 123-456-7890, (123) 456-7890, 1234567890, +1 123 456 7890
        const phoneRegex = /(\+?\d{1,3}[-.\s]?)?(\(?\d{3}\)?[-.\s]?){1,2}\d{4}/g;

        // Regex for addresses (US-style)
        // Matches things like: 123 Main St | 456 Elm Street, Springfield | 789 Broadway Ave, Apt 5, New York
        // TODO: Use a location keyword list to improve detection (i.e. city names, states, countries)
        const addressRegex = /\d{1,5}\s[\w\s]{1,30}\s(?:Street|St|Avenue|Ave|Boulevard|Blvd|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Way|Wy|Plaza|Circle|Ct|Terrace|Ter|Trail|Trl)\b(?:,\s?[\w\s]+)*/gi;

        // 1st wrap URLs with <a> tags
        let temp_result = string.replace(urlRegex, (url) => {

            // If the URL already starts with http:// or https://, leave it
            // Otherwise, prepend http://
            // (purpose: makes sure the link redirects properly)
            const safe_url = /^(https?:\/\/)/i.test(url) ? url : `http://${url}`;

            return `<a href="${safe_url}" class='hoverable_link' target="_blank" rel="noopener noreferrer">${url}</a>`;
        });

        // 2nd replace phone numbers
        temp_result = temp_result.replace(phoneRegex, (phone) => {
            // Remove spaces/dashes for href
            const tel = phone.replace(/[-.\s()]/g, "");
            return `<a href="sms:${tel}" class='hoverable_link'>${phone}</a>`;
        });

        // 3rd replace addresses with Google Maps links
        temp_result = temp_result.replace(addressRegex, (address) => {
            const href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
            return `<a href="${href}" class='hoverable_link' target="_blank" rel="noopener noreferrer">${address}</a>`;
        });

        return temp_result;
    }

    // Checks if the user has already liked the post
    if(auth.user && !checkLike) {
        if(postDoc.likes.filter(like => like.user._id.toString() === auth.user_id).length > 0){
            setLiked(true);
        }
        
        // Marks that the check for likes has been performed
        setCheckLike(true);
    }  

    // Converts the DraftJS text to HTML and sets the converted text
    useEffect(() => {
        if (postDoc && !gotText) {
            let unRawState;
            let unRawDescription;

            if(postDoc.text) {
                try {
                    // Convert stringified DraftJS JSON to a raw JS object
                    const rawBlocksData = JSON.parse(postDoc.text);

                    // Extract just the array of text blocks
                    if (rawBlocksData && Array.isArray(rawBlocksData.blocks)) {
                        unRawState = EditorState.createWithContent(
                            convertFromRaw(rawBlocksData)
                        );

                        unRawDescription = draftToHtml(convertToRaw(unRawState.getCurrentContent()));
                    } else {
                        unRawDescription = postDoc.text;
                    }

                    // -- Detect URLs & Phone #'s in text ---

                    unRawDescription = detect_Links_phoneNumbers_Addresses(unRawDescription);
                    
                    // -- END: Detect URLs & Phone #'s in text ---

                    // -- Text Truncation Functions ---

                    // Check if truncation is needed
                    const plainText = getPlainText(unRawDescription);
                    const shouldTruncate_temp = plainText.length > CHARACTER_LIMIT;
                    const truncated_text = plainText.substring(0, CHARACTER_LIMIT) + '...';
                    setShouldTruncate(shouldTruncate_temp);

                    // -- END: Text Truncation Functions ---

                    setRawTextState(unRawDescription);

                    setTextState(detect_Links_phoneNumbers_Addresses(truncated_text));
                } catch (error) {
                    const text_final = detect_Links_phoneNumbers_Addresses(postDoc.text);
                    
                    setRawTextState(text_final);
                    setTextState(text_final);
                }
            }
            
            // Marks that the text conversion has been performed
            setGotText(true);
        }
    }, [postDoc, gotText]);

    // --- ImageKit (img optimization service) ---
    
    // Replace the firebase URL with ImageKit.io URL-endpoint
    let optimized_post_avatar_path = null;

    if(showGroupName) {
        if(postDoc.group?.avatar) {
            optimized_post_avatar_path = postDoc.group.avatar.replace("https://firebasestorage.googleapis.com","https://ik.imagekit.io/pba3w2st28/");
        }
    } else {
        if(postDoc.user.img) {
            optimized_post_avatar_path = postDoc.user.img.replace("https://firebasestorage.googleapis.com","https://ik.imagekit.io/pba3w2st28/");
        }
    }

    // -- Switch Post Type FOR DISPLAY ---

    // User has joined / left the community post
    const post_type = postDoc?.type?.toLowerCase() || "post";
    if(post_type === "new user" || post_type === "user left") {
        return (
            <div style={{background: '#f5f8fa', padding: '8px 20px', fontSize: '15px'}}>
                <a href={`/user/${postDoc.user._id}`} style={{fontWeight: 500, color: '#000', textDecoration: 'none'}} className='hover_underline'>{capitalizeFirstLetter(postDoc.username)}</a> has {post_type === "new user" ? "joined" : "left"} the community.
            </div>
        )
    }
    
    return (
        <Fragment>
            {/* Render the post component */}
            <div style={(hiddenComments?.length > 0 && hiddenComments?.includes(postDoc._id)) ? {display: 'none'} : {}} className={!(postDoc.commentsOfComments?.length > 0) ? "post" : "post hasPostContainer"}>

                {/* Avatar - left side 3;7 */}
                <div style={{display:'flex', flexDirection:'column'}}>
                    
                    <div className="post__avatar" onClick={showGroupName ? handleGroupVisit : handleProfileVisit}>
                        {optimized_post_avatar_path ? (
                            <Avatar src={optimized_post_avatar_path && optimized_post_avatar_path} />
                        ) : (
                            <DefaultAvatar name={showGroupName ? `${postDoc.group.name}` : `${postDoc.user.first_name} ${postDoc.user.last_name}`} />
                        )}
                    </div>
                    
                    {postDoc.commentsOfComments?.length > 0 && (
                        <div className='post_thread_indicator'></div>
                    )}
                </div>

                {/* Post Main Body - right side 3:7 */}
                <div className="post__body">

                    {/* More Button */}
                    <div style={{display: 'flex'}}>
                        
                        <div onClick={() => !isPreview && setDropdown(!dropdown)} className="post__moreBtn">
                            <MoreHorizIcon />
                        </div>

                        {postDoc.isSponsored && (
                            <span className='post__adLabel icon_16'>
                                Boosted
                                <span style={{width: '3px'}}></span>
                                <TrendingUpIcon />
                            </span>
                        )}
                    </div>

                    {/* Dropdown Menu */}
                    <div className={dropdown ? "edit-dropdown active" : "edit-dropdown"} style={{height: menuHeight}}>
                        <div className="menu">

                            {/* Copy Link Menu Item */}
                            <CopyToClipboard 
                                text={`${website ? website.url : process.env.REACT_APP_DOMAIN}/post/${postDoc._id}?redirect=true`}
                                onCopy={toggleCopiedModal}
                            >
                                <div className="menu-item">
                                    {!copied ? (
                                        <Fragment>
                                            <LinkIcon />
                                            Copy link
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <CheckIcon />
                                            Copied!
                                        </Fragment>
                                    )}
                                </div>
                            </CopyToClipboard>

                            {auth.user && postDoc.user._id !== auth.user._id && (
                                <div onClick={toggleMessageModal} className="menu-item">
                                    <MailIcon />
                                    Send Message
                                </div>
                            )}

                            {/* Delete Menu Item (shown only for authenticated user who created the post) */}
                            {auth.user && postDoc.user._id === auth.user_id ? (
                                <Fragment>
                                    <div onClick={() => window.location.href = `/post/${postDoc._id}/boost`} className="menu-item">
                                        <TrendingUpIcon />
                                        Boost Post
                                    </div>
                                    <div onClick={toggleDeleteModal} className="menu-item danger">
                                        <DeleteIcon />
                                        Delete
                                    </div>
                                </Fragment>
                            ) : null}
                        </div>
                    </div>

                    {/* Hidden Overlay for dropdown */}
                    <div
                        className={`hidden-overlay ${dropdown ? "show" : ""}`}
                        onClick={() => setDropdown(!dropdown)}
                    />

                    {/* Post Header */}
                    <div className="post__header">
                        <div className="post__headerText">
                            <h3 className={parentPost ? 'isReply' : ''}>

                                {/* Post Author name */}
                                {showGroupName ? (
                                    <span onClick={handleGroupVisit} className='hover_underline'>
                                        <span>{capitalizeFirstLetter(postDoc.group.name)}</span>
                                    </span>
                                ) : (
                                    <span onClick={handleProfileVisit} className='hover_underline'>
                                        <span>{postDoc.username ? capitalizeFirstLetter(postDoc.username) : null} {' '}</span>
                                        <Verification_Badge user={postDoc.user} /> <span>{/*TODO: GET RID OF THIS SPAN for some reason it makes the username bold*/}</span> 
                                    </span>
                                )}

                                {/* Post Timestamp */}
                                <span className="post__headerSpecial">
                                    <a href={!isPreview ? `/post/${postDoc._id}` : null}>
                                        {formatDistanceToNow(postDoc.date) === 'less than a minute' ? 'now' : `${formatDistanceToNow(postDoc.date)} ago`}
                                    </a>
                                </span>
                            </h3>
                            
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                {(postDoc.price) && (
                                    <Tag text={postDoc.price} classes={"margin_bottom margin_right"} price_tag />
                                )}
                            </div>

                            {(replyToPost && replyToPost.user?.username)&& (
                                <div className='replying_to_title'>
                                    Replying to {' '}
        
                                    <a href={`/user/${replyToPost.user._id}`}>
                                        {replyToPost.user.first_name} {replyToPost.user.last_name && ' ' + replyToPost.user.last_name}
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Post Text */}
                        {textState !== null && (
                            <div className={`post__headerDescription ${hasImg ? 'withImgs' : ''}`}>
                                {/* <p>{postDoc.text ? postDoc.text : null}</p> */}

                                {!isExpanded && shouldTruncate 
                                    ? <div dangerouslySetInnerHTML={{__html: textState}} /> 
                                    : <div dangerouslySetInnerHTML={{__html: rawTextState}} />}

                                {shouldTruncate && (
                                    <span 
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className='post_see_more_btn'
                                    >
                                        {isExpanded ? 'See less' : 'See more'}
                                    </span>
                                )}
                            </div>
                        )}
                        
                    </div>

                    {/* Post Images */}
                    <ImageBlock 
                        detailPost={postDoc} 
                        setHasImg={setHasImg} 
                        setCheckHasImg={setCheckHasImg} 
                        checkHasImg={checkHasImg} 
                        executeScroll={executeScroll} 
                        redirect_on_click={!isPreview}
                        setImgHeight={setImgHeight}
                        isPreview={isPreview}
                    />
                    {/* {postDoc.img && <img src={postDoc.img} alt="" />} */}

                    {/* Spacer, b/w post images and footer, becuase imageBlock is position:absolute  */}
                    {hasImg && (
                        <div style={{height: `${imgHeight}px`}}/>
                    )}

                    {postDoc.isSponsored && (
                        <Fragment>
                            {postDoc.cta.type === "website" && (
                                <Button onClick={() => handleExternalLink(postDoc.cta.url)} variant="outlined" className="sidebar__tweet cta_btn black" style={{zIndex: 10, position: 'relative'}} fullWidth>
                                    {postDoc.cta.label}
                                    {postDoc.cta.label.toLowerCase() === 'shop now' && (
                                        <Fragment>
                                            <span style={{width: '5px'}}></span>
                                            <LocalMallOutlinedIcon />
                                        </Fragment>
                                    )}
                                    {postDoc.cta.label.toLowerCase() === 'visit website' && (
                                        <Fragment>
                                            <span style={{width: '5px'}}></span>
                                            <LinkOutlinedIcon />
                                        </Fragment>
                                    )}
                                </Button>
                            )}

                            {postDoc.cta.type === "message" && (
                                <Button onClick={toggleMessageModal} variant="outlined" className="sidebar__tweet cta_btn icon_18" style={{zIndex: 10}} fullWidth>
                                    Send Message
                                    <span style={{width: '5px'}}></span>
                                    <MailIcon />
                                </Button>
                            )}
                        </Fragment>

                    )} 
                    
                    {/* Post Actions */}
                    <div className={`post__footer ${postDoc.isSponsored && (postDoc.cta.type === "website" || postDoc.cta.type === "message") ? 'cta_btn' : ''} ${hasImg ? 'hasImg' : ''}`}>

                        {/* Comment Button */}
                        <div>
                            <button 
                                disabled={isPreview}
                                onClick={() => window.location.href = `/post/${postDoc._id}`}
                                className="secondaryBtn post"
                            >
                                <div>
                                    {/* Icon */}
                                    <ChatBubbleOutlineIcon fontSize="small" />

                                    {/* Button Text */}
                                    <span>Comment</span>
                                </div>
                            </button>
                        </div>

                        {/* Like Button */}

                        <div className={liked ? "active" : "" } onClick={() => handleLike(postDoc._id)}>
                            {!isPreview ? (
                                <button className={liked ? "secondaryBtn post active like" : "secondaryBtn post like" }>
                                    <div>
                                        {/* Icon */}
                                        {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}

                                        {/* Button Text & like count */}
                                        <span>{liked ? 'Saved!' : 'Save'} {postDoc.likes.length > 0 && (
                                            <span style={liked ? {display:'none'}: {fontSize:'11px'}}>({postDoc.likes.length})</span>
                                        )}</span>
                                    </div>
                                </button>
                            ) : (
                                <button className="secondaryBtn post like">
                                    <div>
                                        {/* Icon */}
                                        <FavoriteBorderIcon fontSize="small" />

                                        {/* Button Text & like count */}
                                        <span>Save</span>
                                    </div>
                                </button>
                            )}
                        </div>

                        {/* Share Button */}
                        <div style={{position: 'relative'}}>
                            <button disabled={isPreview} onClick={() => setShareDropdown(!shareDropdown)} className="secondaryBtn post share">
                                <div>
                                    {/* Icon */}
                                    <ShareIcon fontSize="small" />

                                    {/* Button Text */}
                                    <span>Share</span>
                                </div>
                            </button>

                            {/* Share Dropdown */}
                            <div className={shareDropdown ? "edit-dropdown active" : "edit-dropdown"} style={{height: menuHeight}}>
                                <div className="menu">

                                    {/* Copy Link Menu Item */}
                                    <CopyToClipboard 
                                        text={`${website ? website.url : process.env.REACT_APP_DOMAIN}/post/${postDoc._id}?redirect=true`}
                                        onCopy={handleShareCopy}
                                    >
                                        <div className="menu-item">
                                            {!copied ? (
                                                <Fragment>
                                                    <LinkIcon />
                                                    Copy link
                                                </Fragment>
                                            ) : (
                                                <Fragment>
                                                    <CheckIcon />
                                                    Copied!
                                                </Fragment>
                                            )}
                                        </div>
                                    </CopyToClipboard>
                                </div>
                            </div>

                            {/* Hidden Overlay for share dropdown */}
                            <div
                                className={`hidden-overlay ${shareDropdown ? "show" : ""}`}
                                onClick={() => setShareDropdown(!shareDropdown)}
                            />
                        </div>
                    
                    </div>
                </div>

                {/* Post Overlay to detect when clicked and redirect to postPage */}
                <div 
                    className="post__overlay" 
                    onClick={() => !isPreview && (window.location.href = `/post/${postDoc._id}`)}
                ></div>
            </div>

            {(show_pro_tools && auth.user && postDoc.user._id === auth.user_id) && (
                <div style={{height: '50px', width: '100%', padding: '0 10px', boxSizing: 'border-box', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', borderBottom: '1px solid #e6ecf0'}}>
                    {/* <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                        <button className="secondaryBtn see_through">
                            View analytics
                        </button>
                    </div> */}
                    <div>
                        <button onClick={() => window.location.href = `/post/${postDoc._id}/boost`} className="secondaryBtn darker icon_white icon_noMargin">
                            Boost Post
                            <span style={{width: '5px'}}></span>
                            <TrendingUpIcon />
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Post Modal */}
            <ModalContainer show={deleteModal} onClose={toggleDeleteModal}>
                <Modal>

                    {/* Modal Title */}
                    <div>
                        <h2>Delete Post?</h2>
                    </div>

                    {/* Modal Description */}
                    <p>This can’t be undone and it will be removed from your profile, the main feed, and from any search results.</p>
                    
                    {/* Modal Actions */}
                    <Button 
                        onClick={handleDelete}
                        variant="outlined" 
                        className="sidebar__tweet danger"
                        fullWidth
                    >
                        Delete
                    </Button>
                    <Button 
                        onClick={toggleDeleteModal}
                        variant="outlined" 
                        className="sidebar__tweet ghost blackText"
                        fullWidth
                    >
                        Cancel
                    </Button>
                </Modal>
            </ModalContainer>

            {/* Copied Link Modal */}
            <ModalContainer show={copiedModal} onClose={toggleCopiedModal} white>
                <Modal border>

                    {/* Modal Title */}
                    <div>
                        <h2>Link Copied!</h2>
                    </div>

                    {/* Modal Description */}
                    <p>You can share this link with anyone.</p>

                    {/* Modal Action */}
                    <Button 
                        onClick={toggleCopiedModal}
                        variant="outlined" 
                        className="sidebar__tweet ghost"
                        fullWidth
                    >
                        Close
                    </Button>
                </Modal>
            </ModalContainer>

            {/* Create Message confirmation Modal */}  
            <ModalContainer show={messageModal} onClose={toggleMessageModal}>
                <Modal>

                    {/* Modal Title */}
                    <div>
                        <h2>Start Conversation?</h2>
                    </div>

                    {/* Modal Description */}
                    <p>Start a private conversation with <b style={{color: '#333'}}>{postDoc.username ? postDoc.username : 'this person'}</b></p>
                    
                    {/* Modal Actions */}
                    <Button 
                        onClick={continueMessage}
                        variant="outlined" 
                        className="sidebar__tweet black"
                        fullWidth
                    >
                        Start Conversation
                    </Button>
                    <Button 
                        onClick={toggleMessageModal}
                        variant="outlined" 
                        className="sidebar__tweet ghost blackText"
                        fullWidth
                    >
                        Cancel
                    </Button>
                </Modal>
            </ModalContainer>
        </Fragment>
    )
}

Post.propTypes = {
    getUserById: PropTypes.func.isRequired, // Required prop: Function to get user by ID
    addLike: PropTypes.func.isRequired, // Required prop: Function to add a like
    deletePost: PropTypes.func.isRequired, // Required prop: Function to delete a post
    auth: PropTypes.object.isRequired, // Required prop: Object representing the authenticated user
    post: PropTypes.object.isRequired,
    nav: PropTypes.object.isRequired,
    setAlert: PropTypes.func.isRequired, // Required prop: Function to set an alert
    createChat: PropTypes.func.isRequired,
    addMessage: PropTypes.func.isRequired,
    toggleAuthModal: PropTypes.func.isRequired // Required prop: Function to toggle the authentication modal
}

const mapStateToProps = state => ({
    // Mapping the auth state from the Redux store to the 'auth' prop
    auth: state.auth,
    post: state.post,
    nav: state.nav
});

export default connect(mapStateToProps, { 

    // Connecting actions from redux to the component
    getUserById, 
    addLike, 
    deletePost, 
    setAlert, 
    createChat,
    addMessage,
    toggleAuthModal 
})(Post);
